/**
 * The tree as it grows.
 *
 * This panel is the throughline of the walkthrough: the same structure is on
 * screen the entire time, gaining nodes as the user advances. Clicking a node
 * shows everything about it — the plaintext it encodes, the MessagePack bytes,
 * its CHK key and the blob hash it is addressed by.
 */

import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FileText, Folder, Layers, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatBytes, toHex } from "@/hashtree/bytes";
import { decodeNode } from "@/hashtree/msgpack";
import type { BuiltNode, NodeKind } from "@/hashtree/nodes";
import { ByteView, Field, FieldGroup, TextPreview } from "./HashDisplay";
import { Prop } from "./Prop";
import { JsonBlock } from "./CodeBlock";

const KIND_META: Record<NodeKind, { icon: typeof Folder; type: number; label: string; className: string }> = {
  blob: { icon: Package, type: 0, label: "blob", className: "text-emerald-600 dark:text-emerald-400" },
  file: { icon: FileText, type: 1, label: "file manifest", className: "text-violet-600 dark:text-violet-400" },
  directory: { icon: Folder, type: 2, label: "directory", className: "text-sky-600 dark:text-sky-400" },
  fanout: { icon: Layers, type: 3, label: "fanout", className: "text-orange-600 dark:text-orange-400" },
};

interface TreeRowProps {
  node: BuiltNode;
  depth: number;
  selectedId: string | null;
  onSelect: (node: BuiltNode) => void;
  /** Name the parent link gave this node, when it has one. */
  name?: string;
}

function TreeRow({ node, depth, selectedId, onSelect, name }: TreeRowProps) {
  const [open, setOpen] = useState(true);
  const meta = KIND_META[node.kind];
  const Icon = meta.icon;
  const hasChildren = node.children.length > 0;
  const selected = selectedId === node.id;

  return (
    <div>
      <div
        className={cn(
          "flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 text-sm transition-colors",
          selected ? "bg-primary/10 ring-1 ring-primary/30" : "hover:bg-muted",
        )}
        style={{ paddingLeft: `${depth * 0.85 + 0.375}rem` }}
        onClick={() => onSelect(node)}
      >
        <button
          type="button"
          aria-label={open ? "Collapse" : "Expand"}
          className={cn("shrink-0 text-muted-foreground", !hasChildren && "invisible")}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((value) => !value);
          }}
        >
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>

        <Icon className={cn("size-3.5 shrink-0", meta.className)} />

        <span className="min-w-0 flex-1 truncate">{name ?? node.label}</span>

        <span className="shrink-0 font-mono text-[0.65rem] text-muted-foreground">{formatBytes(node.size)}</span>
      </div>

      {open &&
        node.children.map((child, index) => (
          <TreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            name={node.links[index]?.n}
          />
        ))}
    </div>
  );
}

function NodeDetail({ node }: { node: BuiltNode }) {
  const meta = KIND_META[node.kind];

  // Manifests decode back to something readable; raw blobs do not.
  const decoded = useMemo(() => {
    if (node.kind === "blob") return null;
    try {
      const parsed = decodeNode(node.plaintext);
      return {
        l: parsed.l.map((link) => ({
          h: toHex(link.h),
          ...(link.k ? { k: toHex(link.k) } : {}),
          ...(link.m ? { m: link.m } : {}),
          ...(link.n !== undefined ? { n: link.n } : {}),
          s: link.s,
          t: link.t,
        })),
        t: parsed.t,
      };
    } catch {
      return null;
    }
  }, [node]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <meta.icon className={cn("size-4", meta.className)} />
        <span className="text-sm font-medium">{node.label}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[0.65rem] text-muted-foreground">
          <Prop name="t" value={meta.type} /> {meta.label}
        </span>
      </div>

      <FieldGroup>
        <Field
          label="blob hash"
          value={toHex(node.encrypted.blobHash)}
          tone="hash"
          hint="SHA256(ciphertext) — what the server stores this under"
        />
        <Field
          label="chk key"
          value={toHex(node.encrypted.chkKey)}
          tone="key"
          hint="SHA256(plaintext) — a bearer secret, held only by the parent link"
        />
        <Field label="plaintext size" value={`${node.plaintext.length} bytes`} copyable={false} />
        <Field label="ciphertext size" value={`${node.encrypted.ciphertext.length} bytes`} copyable={false} />
      </FieldGroup>

      {node.kind === "blob" ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Chunk contents</p>
          <TextPreview bytes={node.plaintext} />
        </div>
      ) : (
        decoded && <JsonBlock value={decoded} caption="decoded manifest" />
      )}

      <ByteView bytes={node.plaintext} label={node.kind === "blob" ? "plaintext bytes" : "MessagePack bytes"} />
      <ByteView bytes={node.encrypted.ciphertext} label="ciphertext (uploaded)" maxHeight="7rem" />
    </div>
  );
}

export function TreeInspector({ root, emptyHint }: { root: BuiltNode | null; emptyHint?: string }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = useMemo(() => {
    if (!root) return null;
    const find = (node: BuiltNode): BuiltNode | null => {
      if (node.id === selectedId) return node;
      for (const child of node.children) {
        const hit = find(child);
        if (hit) return hit;
      }
      return null;
    };
    return find(root) ?? root;
  }, [root, selectedId]);

  if (!root) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        {emptyHint ?? "Your tree will appear here as you build it."}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border bg-card p-1.5">
        <TreeRow node={root} depth={0} selectedId={selected?.id ?? null} onSelect={(node) => setSelectedId(node.id)} />
      </div>
      {selected && <NodeDetail node={selected} />}
    </div>
  );
}
